import React, { useState, useEffect, useCallback } from 'react';
import { PauseCircle, Play, Refresh } from '@saleshandy/icons';
import { ProgressBar } from '@saleshandy/design-system';
import TestReportSummaryLoader from './components/test-report-summary-loader';
import TestRunOnDropdown from './components/test-run-on-dropdown/test-run-on-dropdown';
import InboxPlacementScoreCard from './components/inbox-placement-score-card/inbox-placement-score-card';
import KeySummary from './components/key-summary';
import {
  TestConfigResponse,
  TestReportSummaryResponse,
  SpamAssassinScore,
  TestReportEspsResponse,
} from '../../../../types';
import {
  EmailReputationStatus,
  SpamAssassinScoreReportFilter,
  TestStatus,
  TestTemplateAction,
  TestType,
} from '../../../../enums/inbox-radar';
import SpamAssassinReportModal from './components/sender-email-accounts/components/spam-assassin-report-modal';
import EmailAuthenticationReportModal from './components/sender-email-accounts/components/email-authentication-report-modal/email-authentication-report-modal';
import { RequestStatus } from '../../../../../../shared/enums/request-status';
import { getIsRequestPending } from '../../../../../../shared/utils';
import {
  OverlayTooltip,
  Placement,
} from '../../../../../../shared/design-system/components/overlay';
import { accessibleOnClick } from '../../../../../../shared/utils/accessible-on-click';
import ESPPlacementReport from './components/esp-placement-report';
import AccountResults from './components/account-results';
import { useFirebaseAnimation } from './components/utils/use-firebase-animation';

const TestReportContent: React.FC<{
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  isLoading: boolean;
  testConfigData?: TestConfigResponse;
  testReportSummaryData?: TestReportSummaryResponse;
  testReportListData?: any;
  testReportLastRunList?: any;
  selectedTestId?: string;
  onSelectedTestIdChange?: (testId: string) => void;
  forShareableReport?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  handleCheckLatestStatus?: () => void;
  handleReRunTest?: () => void;
  emailReputationRequestStatus?: RequestStatus;
  emailReputationRequestError?: any;
  hasMoreEmailReputation?: boolean;
  emailAuthenticationReportResponse?: any;
  sendGetEmailAuthenticationRequest?: (
    seedTestId: string,
    payload: {
      senderEmailAccountIds?: number[];
      limit: number;
      page: number;
      reputationStatus?: EmailReputationStatus;
    },
  ) => void;
  testId?: string;
  spammyEmailAccountsResponse?: any;
  sendGetSpammyEmailAccountsRequest?: (seedTestId: string) => void;
  spamAssassinReportResponse?: any;
  sendGetSpamAssassinReportRequest?: (
    seedTestId: string,
    payload: { seedTestSenderEmailAccountId: number },
    filter?: SpamAssassinScoreReportFilter,
  ) => void;
  spamAssassinReportRequestStatus?: RequestStatus;
  spammyEmailAccountsRequestStatus?: RequestStatus;
  resetSpammyEmailAccountsRequest?: () => void;
  resetSpamAssassinReportRequest?: () => void;
  handlePauseResumeTest?: (action: TestTemplateAction) => void;
  testReportEspsData?: TestReportEspsResponse;
  firebaseData?: any;
  isAlertStatusDelayActive?: boolean;
}> = ({
  scrollContainerRef,
  isLoading,
  testConfigData,
  testReportSummaryData,
  testReportListData,
  testReportLastRunList,
  selectedTestId,
  onSelectedTestIdChange,
  forShareableReport = false,
  onLoadMore,
  isLoadingMore,
  handleCheckLatestStatus,
  emailReputationRequestStatus,
  emailReputationRequestError,
  hasMoreEmailReputation,
  emailAuthenticationReportResponse,
  sendGetEmailAuthenticationRequest,
  testId,
  spammyEmailAccountsResponse,
  sendGetSpammyEmailAccountsRequest,
  spamAssassinReportResponse,
  sendGetSpamAssassinReportRequest,
  spamAssassinReportRequestStatus,
  spammyEmailAccountsRequestStatus,
  resetSpammyEmailAccountsRequest,
  resetSpamAssassinReportRequest,
  handlePauseResumeTest,
  testReportEspsData,
  firebaseData,
  isAlertStatusDelayActive,
}) => {
  const [
    showSpamAssassinReportModal,
    setShowSpamAssassinReportModal,
  ] = useState(false);
  const [
    spamAssassinReportModalData,
    setSpamAssassinReportModalData,
  ] = useState<SpamAssassinScore>(null);
  const [
    showEmailAuthenticationReportModal,
    setShowEmailAuthenticationReportModal,
  ] = useState(false);
  const [
    selectedEmailAccountId,
    setSelectedEmailAccountId,
  ] = useState<number>();
  const [isKeySummaryModal, setIsKeySummaryModal] = useState(false);
  const [currentFromEmail, setCurrentFromEmail] = useState<string>('');

  // Add pagination state for email authentication
  const [emailAuthenticationPage, setEmailAuthenticationPage] = useState(1);
  const [
    isLoadingMoreEmailAuthentication,
    setIsLoadingMoreEmailAuthentication,
  ] = useState(false);

  // State to track if sentCount should show animation
  const { shouldAnimateItem } = useFirebaseAnimation(firebaseData, {
    duration: 2000,
    fields: ['placementCount'],
  });

  const shouldAnimateSentCount = shouldAnimateItem('sentCount');

  // Get the first available spam assassin data from the email accounts
  const getFirstSpamAssassinData = (): SpamAssassinScore | undefined => {
    if (testReportListData?.seedTests?.length > 0) {
      // Find the account with the most detailed spam assassin data
      const accountWithBestData = testReportListData.seedTests.find(
        (account) =>
          account?.spamAssassinScore?.score !== undefined &&
          account?.spamAssassinScore?.spamReport?.tests?.length > 0,
      );

      if (accountWithBestData?.spamAssassinScore) {
        return {
          ...accountWithBestData.spamAssassinScore,
          fromEmail: accountWithBestData.fromEmail,
        };
      }

      // Fallback to any account with spam assassin data
      const accountWithData = testReportListData.seedTests.find(
        (account) => account?.spamAssassinScore?.score !== undefined,
      );

      if (accountWithData?.spamAssassinScore) {
        return {
          ...accountWithData.spamAssassinScore,
          fromEmail: accountWithData.fromEmail,
        };
      }
    }
    return undefined;
  };

  const handleSpamAssassinModalOpen = (
    data: SpamAssassinScore,
    fromKeySummary?: boolean,
  ) => {
    setSpamAssassinReportModalData(data);
    setShowSpamAssassinReportModal(true);
    setIsKeySummaryModal(fromKeySummary || false);

    if (fromKeySummary && data?.fromEmail) {
      setCurrentFromEmail(data.fromEmail);
    }

    // Call the spammy email accounts API when modal is opened
    if (testId) {
      sendGetSpammyEmailAccountsRequest(testId);
    }
  };

  const handleSpamAssassinModalClose = () => {
    setShowSpamAssassinReportModal(false);
    resetSpammyEmailAccountsRequest();
    resetSpamAssassinReportRequest();
    setSelectedEmailAccountId(null);
    setIsKeySummaryModal(false);
    setCurrentFromEmail('');
  };

  const handleEmailAccountChange = useCallback(
    (emailAccountId: number) => {
      setSelectedEmailAccountId(emailAccountId);
      if (testId) {
        sendGetSpamAssassinReportRequest(
          testId,
          {
            seedTestSenderEmailAccountId: emailAccountId,
          },
          SpamAssassinScoreReportFilter.Positive,
        );
      }
    },
    [testId, sendGetSpamAssassinReportRequest],
  );

  const handleEmailAuthenticationModalOpen = () => {
    setShowEmailAuthenticationReportModal(true);
    // Reset pagination when modal is opened
    setEmailAuthenticationPage(1);

    // Call the email authentication API when modal is opened
    if (testId) {
      sendGetEmailAuthenticationRequest(testId, {
        limit: 15,
        page: 1,
      });
    }
  };

  // Function to handle loading more email authentication data
  const handleLoadMoreEmailAuthentication = useCallback(() => {
    if (hasMoreEmailReputation) {
      setIsLoadingMoreEmailAuthentication(true);
      const nextPage = emailAuthenticationPage + 1;
      setEmailAuthenticationPage(nextPage);

      sendGetEmailAuthenticationRequest(testId, {
        limit: 15,
        page: nextPage,
      });
    }
  }, [
    testId,
    hasMoreEmailReputation,
    isLoadingMoreEmailAuthentication,
    emailAuthenticationPage,
    testReportListData,
    sendGetEmailAuthenticationRequest,
  ]);

  const handlePauseResumeButtonClick = () => {
    // Use Firebase data if available, fallback to current data
    const currentTestStatus =
      firebaseData?.testStatus || testConfigData?.testStatus;

    if (currentTestStatus === TestStatus.Active) {
      handlePauseResumeTest(TestTemplateAction.Pause);
    } else if (
      currentTestStatus === TestStatus.Paused ||
      currentTestStatus === TestStatus.Stopped
    ) {
      handlePauseResumeTest(TestTemplateAction.Resume);
    }
  };

  useEffect(() => {
    if (
      spammyEmailAccountsResponse &&
      spammyEmailAccountsResponse.length > 0 &&
      !selectedEmailAccountId
    ) {
      const firstEmailAccountId =
        spammyEmailAccountsResponse[0]?.seedTestSenderEmailAccountId;

      if (firstEmailAccountId) {
        setSelectedEmailAccountId(firstEmailAccountId);
      }
    }
  }, [spammyEmailAccountsResponse, selectedEmailAccountId]);

  // Effect to handle email authentication loading state
  useEffect(() => {
    if (emailReputationRequestStatus === RequestStatus.Succeeded) {
      setIsLoadingMoreEmailAuthentication(false);
    }
  }, [emailReputationRequestStatus]);

  const getTestStatusAction = () => {
    // Use Firebase data if available, fallback to current data
    const currentTestStatus =
      firebaseData?.testProgress || testConfigData?.testStatus;
    const currentTestType = firebaseData?.type || testConfigData?.type;

    if (
      currentTestStatus === TestStatus.Active ||
      currentTestStatus === TestStatus.Paused ||
      currentTestStatus === TestStatus.Stopped
    ) {
      if (currentTestType === TestType.RECURRING) {
        return (
          <OverlayTooltip
            text={
              currentTestStatus === TestStatus.Active
                ? 'Pausing will stop the current test only. New tests (if scheduled) will still be created.'
                : 'Resuming will continue the current test from where it was paused.'
            }
            placement={Placement.Bottom}
            className="test-report-status-action-tooltip"
          >
            <div
              className="test-report-status-action-btn"
              {...accessibleOnClick(handlePauseResumeButtonClick)}
            >
              {currentTestStatus === TestStatus.Active ? (
                <>
                  <PauseCircle height={20} width={20} />
                  Pause Test
                </>
              ) : (
                <>
                  <Play height={20} width={20} />
                  Resume Test
                </>
              )}
            </div>
          </OverlayTooltip>
        );
      }
      return (
        <div
          className="test-report-status-action-btn"
          {...accessibleOnClick(handlePauseResumeButtonClick)}
        >
          {currentTestStatus === TestStatus.Active ? (
            <>
              <PauseCircle height={20} width={20} />
              Pause Test
            </>
          ) : (
            <>
              <Play height={20} width={20} />
              Resume Test
            </>
          )}
        </div>
      );
    }
    return null;
  };

  if (isLoading && !isLoadingMore) {
    return <TestReportSummaryLoader />;
  }

  return (
    <div className="test-report-summary-container">
      <div
        className={`test-report-summary-header ${
          testConfigData?.testStatus === TestStatus.Active ? 'active-test' : ''
        }`}
      >
        <div className="test-report-summary-header-left-container">
          <TestRunOnDropdown
            testRunOnList={testReportLastRunList}
            selectedTestId={selectedTestId}
            onTestIdChange={onSelectedTestIdChange}
            hideTransfer={forShareableReport}
            lastRunOn={testConfigData?.activatedAt}
            showDropdown={testConfigData?.type === TestType.RECURRING}
          />
          {forShareableReport ? null : getTestStatusAction()}
        </div>

        {/* Progress Bar Section */}
        <div className="d-flex align-items-center gap-8 test-report-email-sent">
          <span className="email-sent">Email Sent</span>
          <div className="test-progress-bar">
            <ProgressBar
              percentage={Math.round(
                (() => {
                  // Use Firebase data if available, fallback to current data
                  const firebaseSentCount =
                    firebaseData?.summary?.placementCount;
                  const firebaseTotalEmails =
                    firebaseData?.summary?.totalEmails;

                  if (
                    firebaseSentCount !== undefined &&
                    firebaseTotalEmails !== undefined
                  ) {
                    return (firebaseSentCount / firebaseTotalEmails) * 100;
                  }

                  // Fallback to current data
                  return (
                    (testReportSummaryData?.inboxPlacement?.sentCount /
                      testReportSummaryData?.inboxPlacement?.totalEmails) *
                    100
                  );
                })(),
              )}
            />
            <div className="d-flex align-items-baseline gap-4">
              <span
                className={`test-sent-count ${
                  shouldAnimateSentCount ? 'esp-count-pop-animation' : ''
                }`}
              >
                {(() => {
                  // Use Firebase data if available, fallback to current data
                  const firebaseSentCount =
                    firebaseData?.summary?.placementCount;
                  if (firebaseSentCount !== undefined) {
                    return firebaseSentCount || 0;
                  }
                  return testReportSummaryData?.inboxPlacement?.sentCount;
                })()}
              </span>
              <span className="test-count-divider">/</span>
              <span className="test-total-count">
                {(() => {
                  // Use Firebase data if available, fallback to current data
                  const firebaseTotalEmails =
                    firebaseData?.summary?.totalEmails;
                  if (firebaseTotalEmails !== undefined) {
                    return firebaseTotalEmails;
                  }
                  return testReportSummaryData?.inboxPlacement?.totalEmails;
                })()}
              </span>
            </div>
          </div>
          {testConfigData &&
            testConfigData?.testStatus === TestStatus.Active &&
            !forShareableReport && (
              <OverlayTooltip
                text="Refresh Test Progress"
                placement={Placement.Bottom}
              >
                <div
                  className="test-report-refresh-icon-button"
                  {...accessibleOnClick(handleCheckLatestStatus)}
                >
                  <Refresh height={18} width={18} />
                </div>
              </OverlayTooltip>
            )}
        </div>
      </div>
      <div className="test-report-summary">
        <div className="test-report-summary-content-container">
          <InboxPlacementScoreCard
            inboxPlacement={testReportSummaryData?.inboxPlacement}
            testConfigData={testConfigData}
            handleCheckLatestStatus={handleCheckLatestStatus}
            firebaseData={firebaseData}
            isStatusDelayActive={isAlertStatusDelayActive}
            forShareableReport={forShareableReport}
          />
          {testReportEspsData?.length > 0 && (
            <ESPPlacementReport
              testReportEspsData={testReportEspsData}
              firebaseData={firebaseData}
              sentCount={testReportSummaryData?.inboxPlacement?.sentCount}
            />
          )}
          {testReportSummaryData?.summary?.length > 0 && (
            <KeySummary
              keySummaryList={testReportSummaryData?.summary}
              onSpamAssassinModalOpen={handleSpamAssassinModalOpen}
              onEmailAuthenticationModalOpen={
                handleEmailAuthenticationModalOpen
              }
              spamAssassinData={getFirstSpamAssassinData()}
              testConfigData={testConfigData}
              forShareableReport={forShareableReport}
            />
          )}
          {testReportListData && (
            <AccountResults
              error={emailReputationRequestError}
              testConfigData={testConfigData}
              testReportListData={testReportListData}
              testReportSummaryData={testReportSummaryData}
              scrollContainerRef={scrollContainerRef}
              testReportSenderEmailAccountList={testReportListData?.seedTests}
              onLoadMore={onLoadMore}
              isLoadingMore={isLoadingMore}
              forShareableReport={forShareableReport}
              testId={testId}
              sendGetSpamAssassinReportRequest={
                sendGetSpamAssassinReportRequest
              }
              spamAssassinReportResponse={spamAssassinReportResponse}
              isLoadingSpamAssassinReport={getIsRequestPending(
                spamAssassinReportRequestStatus,
              )}
              sendGetEmailAuthenticationRequest={
                sendGetEmailAuthenticationRequest
              }
              emailAuthenticationReportResponse={
                emailAuthenticationReportResponse
              }
              firebaseData={firebaseData}
              emailReputationRequestStatus={emailReputationRequestStatus}
            />
          )}
        </div>
      </div>
      <SpamAssassinReportModal
        show={showSpamAssassinReportModal}
        onClose={handleSpamAssassinModalClose}
        data={spamAssassinReportResponse?.spamAssassinScore}
        spammyEmailAccounts={spammyEmailAccountsResponse?.map((item) => ({
          fromEmail: item.fromEmail,
          seedTestSenderEmailAccountId: item?.seedTestSenderEmailAccountId,
          isDeleted: item.isDeleted,
        }))}
        selectedEmailAccountId={selectedEmailAccountId}
        onEmailAccountChange={handleEmailAccountChange}
        isLoading={
          getIsRequestPending(spamAssassinReportRequestStatus) ||
          getIsRequestPending(spammyEmailAccountsRequestStatus)
        }
        isEmailLoading={getIsRequestPending(spammyEmailAccountsRequestStatus)}
        isFromEmail={currentFromEmail}
      />
      <EmailAuthenticationReportModal
        show={showEmailAuthenticationReportModal}
        onClose={() => setShowEmailAuthenticationReportModal(false)}
        emailReputationData={emailAuthenticationReportResponse?.emailAccounts}
        hasMoreEmailReputation={hasMoreEmailReputation}
        isLoading={getIsRequestPending(emailReputationRequestStatus)}
        error={emailReputationRequestError}
        onLoadMore={handleLoadMoreEmailAuthentication}
        isLoadingMore={isLoadingMoreEmailAuthentication}
      />
    </div>
  );
};

export default TestReportContent;
